import { App } from "@capacitor/app";
import { useDialogStore } from "@eventr/common-storybook";
import i18n from "i18n/i18next";
import { useEffect } from "react";
import { bs, useBoundStore } from "state/bound.store";
import { useConnectivityStore } from "../connectivity/connectivity.store";
import { selectNotesEnabled } from "./use-notes";

/**
 * Replays queued note changes and refreshes the selected itinerary's notes on mount, on
 * sign-in, on switching itinerary, when a connection returns and when the app comes to the
 * foreground. Tells the user when a sync finds one of their notes removed.
 */
export const NoteSyncManager = () => {
  const removedTitles = useBoundStore((s) => s.removedNoteTitles);
  const replaceDialog = useDialogStore((s) => s.replaceDialog);
  const clearDialog = useDialogStore((s) => s.clearDialog);

  useEffect(() => {
    const flush = async () => {
      if (!useConnectivityStore.getState().isConnectivityAvailable) return;
      if (!bs().loginCompleted) return;

      // Queued changes go up before the server's list comes down, so a list that predates
      // an offline change cannot overwrite it.
      await bs().syncNotes();
      if (selectNotesEnabled(bs())) await bs().loadNotes(bs().selectedItinID);
    };

    void flush();

    const unsubscribeConnectivity = useConnectivityStore.subscribe(
      (state) => state.isConnectivityAvailable,
      (available) => {
        if (available) void flush();
      },
    );
    const unsubscribeLogin = useBoundStore.subscribe(
      (state) => state.loginCompleted,
      (completed) => {
        if (completed) void flush();
      },
    );
    const unsubscribeItinerary = useBoundStore.subscribe(
      (state) => state.selectedItinID,
      () => void flush(),
    );
    const listener = App.addListener("appStateChange", (state) => {
      if (state.isActive) void flush();
    });

    return () => {
      unsubscribeConnectivity();
      unsubscribeLogin();
      unsubscribeItinerary();
      void listener.then((handle) => handle.remove());
    };
  }, []);

  useEffect(() => {
    if (removedTitles.length === 0) return;
    replaceDialog({
      kind: "info",
      text: i18n.t("notes.removed", { count: removedTitles.length }),
      onClose: () => clearDialog(),
      backdropClosable: true,
    });
    bs().clearRemovedNoteTitles();
  }, [removedTitles, replaceDialog, clearDialog]);

  return null;
};
